import { draftRouteState } from "./routeComposerState";
import { dayMapState, fieldMapState } from "./dayMapState";
import { atom, atomFamily } from "recoil";
import { selector, selectorFamily } from "./recoilSelectors";
import { dayState, pageState, selectedDayState } from "./appState";
import { atomWithIndexedPersistence, idbStorageEffect } from "../utils/persistence";
import { JobWithEtaEtd, RouteInfo } from "../types/job";
import { boardMemberUidsState } from "./boardState";
import { jobsByMemberDayState } from "./jobsState";
import { dayOfToday } from "../utils/time";
import { PAGE } from "../constants/pages";
import { isMultiTracksDatesState, tracksSelectedUidsState } from "./tracksState";
import { TrackEntry } from "../types/track";
import { matchesAnyTrackEntry } from "../utils/trackUtils";
import { memberState } from "./membersState";
import { zonesSelectedUidsState } from "./zoneState";
import { Day, Uid } from "../types/core";
import { routingDirectionsState, routingHoverState, routingSelectedState } from "./viewState";
import {
    GeoJSONFeatureCollectionPlaces,
    GeoJSONFeatureHeatEntry,
    GeoJSONFeaturePointTrackOrStopEntry
} from "../types/map";
import { placesSelectedUidsState } from "./placeState";
import { MAX_SELECTED_PLACES_COUNT } from "../constants/places";
import { showTracksLayerState } from "./mapState";

export const placesLayerState = atom<GeoJSONFeatureCollectionPlaces | undefined>({
    key: "layer.geojson.places",
    default: undefined,
});
export const bulkPlacesLayerState = selector<GeoJSONFeatureCollectionPlaces | undefined>({
    key: "layer.geojson.places.bulk",
    get: ({ get }) => {
        const placesLayer = get(placesLayerState);
        if (!placesLayer) {
            return;
        }
        const selection = get(placesSelectedUidsState);
        // the place open in the drawer is the one selected, so it shows its radius too
        const highlighted = new Set(selection.length < MAX_SELECTED_PLACES_COUNT ? selection : []);
        const filteredFeatures = highlighted.size > 0
            ? placesLayer.features.filter((f) => highlighted.has(f.properties.uid))
            : [];
        return {
            ...placesLayer,
            features: filteredFeatures,
        }
    },
});

export const placesLayerRetryState = atom({ key: "layer.geojson.places.retry", default: 0 });
export const placesLayerMoreCounterState = atom({ key: "layer.geojson.places.morecounter", default: 0 });
export const placesLayerSkipCounterState = atom({ key: "layer.geojson.places.skipcounter", default: 0 });
// True while an already-visible places grid is being re-synced (edit, create, bulk edit,
// post-import reload). Drives "Refreshing… N currently shown" wording; a plain false means a
// progressive page-load, which reads as "Loading… N loaded".
export const placesLayerRefreshingState = atom({ key: "layer.geojson.places.refreshing", default: false });

export const zonesLayerState = atomWithIndexedPersistence("layer.geojson.zones", undefined as (GeoJSON.FeatureCollection | undefined));

export const selectedZonesLayerState = selector({
    key: "layer.geojson.zones.selected",
    get: ({ get }) => {
        const uids = get(zonesSelectedUidsState);
        const layer = get(zonesLayerState);
        if (!layer?.features) {
            return undefined;
        }
        return {
            ...layer,
            features: layer.features
                .filter(f => uids.includes(f.properties?.uid))
        } as GeoJSON.FeatureCollection;
    }
})

export const waypointsLayerState = atomFamily<GeoJSON.FeatureCollection | undefined, { uid: Uid }>({
        key: "layer.geojson.waypoints",
        default: undefined,
    }
);

export const liveWaypointsDataState = selector<GeoJSONFeaturePointTrackOrStopEntry[]>({
        key: "layer.geojson.livewaypoints",
        get: ({ get }) => {
            const memberUids = get(boardMemberUidsState);
            const collections = memberUids.map((uid) => {
                return get(waypointsLayerState({ uid }));
            }).filter((c) => c !== undefined);
            const features = collections.flatMap((c) => (c?.features ?? []) as GeoJSONFeaturePointTrackOrStopEntry[]);
            return features;
        },
    }
);

export const heatmapLayerState = atomFamily<GeoJSON.FeatureCollection | undefined, {
    member: string | undefined,
    fromDay: Day,
    untilDay: Day
}>({
        key: "layer.geojson.heatmap",
        default: undefined as (GeoJSON.FeatureCollection | undefined),
    }
);

export const heatmapDataState = selectorFamily<GeoJSONFeatureHeatEntry[], { fromDay: Day, untilDay: Day }>({
        key: "layer.geojson.heatmapdata",
        get: ({ fromDay, untilDay }) => ({ get }) => {
            const memberUids = get(boardMemberUidsState);
            if (memberUids.length) {
                const collections = memberUids.map((uid) => {
                    return get(heatmapLayerState({ member: uid, fromDay, untilDay }));
                }).filter((c) => c !== undefined);
                const features = collections.flatMap((c) => (c?.features ?? []) as GeoJSONFeatureHeatEntry[]);
                return features;
            }
            return (get(heatmapLayerState({
                member: undefined,
                fromDay,
                untilDay
            }))?.features ?? []) as GeoJSONFeatureHeatEntry[];
        },
    }
);

export const dayRouteState = atomFamily({
    key: "layer.geojson.dayroute",
    default: "",
    effects: [idbStorageEffect], // maybe delete?
});

export interface FeatureCollectionWithUid extends GeoJSON.FeatureCollection {
    uid: Uid;
    color?: string;
}

export const directionsLayersState = selector<GeoJSON.FeatureCollection[]>({
    key: 'layer.geojson.directions',
    get: ({ get }) => {
        const routingHover = get(routingHoverState);
        const array: GeoJSON.FeatureCollection[] = [];
        if (routingHover) {
            const json: any = JSON.parse(routingHover);
            const feature = get(routingDirectionsState(routingHover));
            if (feature) {
                array.push({
                    features: [feature],
                    type: "FeatureCollection",
                    uid: json.uid,
                    alpha: 0.5,
                } as GeoJSON.FeatureCollection<GeoJSON.Geometry>)
            }
        }
        const routingSelected = get(routingSelectedState);
        if (routingSelected) {
            const json: any = JSON.parse(routingSelected);
            const feature = get(routingDirectionsState(routingSelected));
            if (feature) {
                array.push({
                    features: [feature],
                    type: "FeatureCollection",
                    uid: json.uid,
                    alpha: 1,
                } as GeoJSON.FeatureCollection<GeoJSON.Geometry>)
            }
        }
        return array;
    }
});

export const selectedDayRoutesState = selector<FeatureCollectionWithUid[]>({
    key: 'layer.geojson.dayroute.selected',
    get: ({ get }) => {
        const selectedDay = get(dayState);
        const uids = get(boardMemberUidsState);
        const page = get(pageState);
        const day = page === PAGE.LIVE ? dayOfToday() : selectedDay;
        // while the New route card is open the map shows only the route being built: the chosen person's day
        // with the new stops in it, or nothing but the stops when it is unassigned
        const draft = get(draftRouteState);
        if (draft) {
            return draft.uid && draft.path.length > 1
                ? [{
                    type: "FeatureCollection",
                    features: [{ type: "Feature", geometry: { type: "LineString", coordinates: draft.path }, properties: {} }],
                    uid: draft.uid,
                    color: get(memberState(draft.uid))?.color,
                } as FeatureCollectionWithUid]
                : [];
        }
        if (day === 0 || uids.length === 0) {
            return [];
        }
        const routes = uids.map(uid => {
                const dayRoute = get(dayRouteState({ uid, day }));
                const color = get(memberState(uid))?.color;
                if (dayRoute && dayRoute.features && dayRoute.features.length > 0) {
                    return { ...dayRoute, uid, color };
                } else {
                    const lines = get(jobsDirectLinesState({ uid, day }));
                    if (lines) {
                        return { ...lines, uid, color };
                    }
                    return undefined;
                }
            }
        ).filter(el => el !== undefined) as FeatureCollectionWithUid[];
        return routes;
    }
});

export const dayRouteInfoState = selectorFamily<RouteInfo | undefined, { uid: Uid; day: Day; }>({
    key: 'layer.geojson.dayroute.routeinfo',
    get: (uidDay) => ({ get }) => {
        const dayRoute = get(dayRouteState(uidDay));
        if (dayRoute && dayRoute.features && dayRoute.features.length > 0) {
            const coord = dayRoute.features[0].geometry.coordinates;
            // a route whose only stop is the start itself is one point, still a route with a summary
            if (coord?.length >= 1 && coord[0]?.length >= 2) {
                const locations = {
                    startLocation: {
                        lat: coord[0][1],
                        lng: coord[0][0],
                    },
                    endLocation: {
                        lat: coord[coord.length - 1][1],
                        lng: coord[coord.length - 1][0],
                    }
                };
                return {
                    ...(dayRoute as any).features[0].properties,
                    ...locations,
                } as RouteInfo;
            }
        }
        return undefined;
    }
});

export const recentTrackLayerState = atom<GeoJSON.FeatureCollection | undefined>({
    key: "layer.geojson.tracks.recent",
    default: undefined,
});


export const tracksLayersState = selector<GeoJSON.FeatureCollection[]>({
    key: 'layer.geojson.tracks',
    get: ({ get }) => {
        const page = get(pageState);
        const isMultiDay = page === PAGE.LOCATION && get(isMultiTracksDatesState);
        // the Day view shows one person's day, so the map shows only theirs
        const dayMap = page === PAGE.LOCATION && !isMultiDay ? get(dayMapState) : undefined;
        const dayLayer = dayMap ? get(memberTracksLayerSingleDayState({ uid: dayMap.uid, day: get(dayState) })) : undefined;
        // the field view shows everyone in it, not only the people on the board
        const fieldMap = page === PAGE.LOCATION && !isMultiDay && !dayMap ? get(fieldMapState) : undefined;
        const layers = dayMap
            ? (dayLayer?.features ? [dayLayer] : [])
            : fieldMap
                ? fieldMap.uids
                    .map(uid => get(memberTracksLayerSingleDayState({ uid, day: get(dayState) })))
                    .filter((layer): layer is GeoJSON.FeatureCollection => !!layer && !!layer.features)
                : get(isMultiDay
                    ? selectedMembersTracksLayerMultiDayState
                    : selectedMembersTracksLayerSingleDayState);
        const selectedTrackUids = get(tracksSelectedUidsState);
        if (selectedTrackUids.length > 0) {
            const trackEntries = layers.flatMap(layer => {
                return layer.features
                    .filter(feature => selectedTrackUids.includes(feature?.properties?.uid))
                    .map(feature => feature.properties as TrackEntry);
            });
            return layers.map(layer => {
                const features = layer.features
                    .filter(feature => selectedTrackUids.includes(feature?.properties?.uid || feature?.properties?.trackUid)
                        || matchesAnyTrackEntry(trackEntries, feature?.properties?.ts)
                    );
                return { ...layer, features };
            }) as GeoJSON.FeatureCollection[];
        } else if (page === PAGE.LIVE || page === PAGE.LOCATION || page === PAGE.DISPATCH) {
            return get(showTracksLayerState) ? layers : [];
        } else {
            return [];
        }
    }
});

export interface ReviewTracksQuery {
    fromDay: Day;
    untilDay: Day;
    /** ApprovalStatus filter */
    approval?: number;
    /** TrackSource filter */
    source?: number;
}

/** Last query sent to the company-wide review scope (member=all); undefined until first loaded. */
export const reviewTracksQueryState = atom<ReviewTracksQuery | undefined>({
    key: "layer.geojson.tracks.review.query",
    default: undefined,
});

/** Result of the company-wide review scope; see services/geojson.ts fetchReviewTracks. */
export const reviewTracksLayerState = atom<GeoJSON.FeatureCollection | undefined>({
    key: "layer.geojson.tracks.review",
    default: undefined,
});

export const memberTracksLayerSingleDayState = atomFamily<GeoJSON.FeatureCollection | undefined, {
    uid: Uid;
    day: Day;
}>({
    key: "layer.geojson.tracks",
    default: undefined as (GeoJSON.FeatureCollection | undefined),
});

export const memberTracksLayerMultiDayState = atomFamily<GeoJSON.FeatureCollection | undefined, { uid: Uid }>({
    key: "layer.geojson.member.tracks",
    default: undefined,
});

export const tracksForLocationTableSpecificMemberState = selectorFamily<GeoJSON.FeatureCollection | undefined, Uid>({
    key: "layer.geojson.tracks.table.member",
    get: (uid: Uid) => ({ get }) => {
        const isMultiTrackDates = get(isMultiTracksDatesState);
        if (isMultiTrackDates) {
            return get(memberTracksLayerMultiDayState({ uid }));
        }
        const day = get(selectedDayState);
        return get(memberTracksLayerSingleDayState({ uid, day }));
    }
});

export const tracksForLocationTableSelectedMembersState = selector<GeoJSON.FeatureCollection[]>({
    key: "layer.geojson.tracks.table.selectedmembers",
    get: ({ get }) => {
        const boardMemberUids = get(boardMemberUidsState);
        const membersTracksLayers = boardMemberUids
            .map(uid => get(tracksForLocationTableSpecificMemberState(uid)))
            .filter(layer => layer && layer.features) as GeoJSON.FeatureCollection[];
        return membersTracksLayers;
    }
});

export const selectedMembersTracksLayerSingleDayState = selector<GeoJSON.FeatureCollection[]>({
    key: 'layer.geojson.tracks.selectedmembers',
    get: ({ get }) => {
        const allMemberUids = get(boardMemberUidsState);
        const day = get(dayState);
        const membersTracksLayers = allMemberUids
            .map(uid => get(memberTracksLayerSingleDayState({ uid, day })))
            .filter(layer => layer && layer.features) as GeoJSON.FeatureCollection[];
        return membersTracksLayers;
    }
});

export const selectedMembersTracksLayerMultiDayState = selector<GeoJSON.FeatureCollection[]>({
    key: 'layer.geojson.member.tracks.selected',
    get: ({ get }) => {
        const boardMemberUids = get(boardMemberUidsState);
        const membersTracksLayers = boardMemberUids
            .map(uid => get(memberTracksLayerMultiDayState({ uid })))
            .filter(layer => layer && layer.features) as GeoJSON.FeatureCollection[];
        return membersTracksLayers;
    }
});

export const allSelectedTracksLayerState = selector<GeoJSON.FeatureCollection[]>({
    key: 'layer.geojson.tracks.all.selected',
    get: ({ get }) => {
        const isMulti = get(isMultiTracksDatesState);
        const membersTracksLayers = get(isMulti
            ? selectedMembersTracksLayerMultiDayState
            : selectedMembersTracksLayerSingleDayState);
        const selectedTracks = get(tracksSelectedUidsState);
        if (selectedTracks.length) {
            const filteredFeatures = membersTracksLayers.map(layer => ({
                ...layer,
                features: layer.features.filter((f) => selectedTracks.includes(f.properties?.uid)),
            }));
            return filteredFeatures;
        }
        return [];
    }
});

export const selectedTimelineState = selectorFamily<GeoJSON.FeatureCollection | undefined, Uid>({
    key: 'layer.geojson.timeline.selected',
    get: (uid) => ({ get }) => {
        const day = get(dayState);
        if (day === 0) {
            return undefined;
        }
        return get(timelineState({ uid: uid, day: day }));
    }
});

export const timelineState = atomFamily<GeoJSON.FeatureCollection | undefined, { uid: Uid; day: Day; }>({
    key: "layer.geojson.timeline",
    default: undefined as (GeoJSON.FeatureCollection | undefined),
});

const jobsDirectLinesState = selectorFamily<GeoJSON.FeatureCollection | undefined, { uid: Uid; day: Day; }>({
    key: 'layers.geojson.jobs.directlines',
    get: ({ uid, day }) => ({ get }) => {
        const sameDayJobs = get(jobsByMemberDayState({ uid, day }))

        const coordinates = sameDayJobs
            .filter(j => j.receiverUid === uid)
            .sort((a, b) => a.number - b.number)
            .map(j => [j.destinationLng, j.destinationLat]);

        if (coordinates.length > 0) {
            const geoJson = {
                features: [
                    {
                        geometry: {
                            coordinates: coordinates,
                            type: "LineString",
                        },
                        type: "Feature",
                    } as GeoJSON.Feature
                ],
                type: "FeatureCollection",
            } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;
            return geoJson;
        }
        return undefined;
    },
});

// Jobs with pre-calculated ETA/ETD for a week
export const jobsWithEtaEtdForWeekState = selectorFamily<JobWithEtaEtd[], { uid: Uid; startDay: Day; endDay: Day }>({
    key: 'jobs.etaEtd.member.week',
    get: ({ uid, startDay, endDay }) => ({ get }) => {
        const results: JobWithEtaEtd[] = [];
        
        const startDayNum = Number(startDay);
        const endDayNum = Number(endDay);
        
        for (let dayNum = startDayNum; dayNum <= endDayNum; dayNum++) {
            const day = dayNum as Day;
            const jobs = get(jobsByMemberDayState({ uid, day }));
            const routeInfo = get(dayRouteInfoState({ uid, day }));
            
            // Validate route info matches job count
            const validRouteInfo = routeInfo && routeInfo.metaOnSiteTimesInSec.length === jobs.length 
                ? routeInfo 
                : undefined;
            
            jobs.forEach(job => {
                let calculatedStartTs: number;
                let calculatedEndTs: number;
                let hasRouteInfo = false;
                
                if (validRouteInfo && job.number > 0) {
                    // Calculate ETA and ETD from route info
                    const { eta, etd } = calcEtaEtdForJob(validRouteInfo, job.number);
                    
                    // Convert seconds since midnight to millisecond timestamp
                    const dayStartTs = dayToMillis(day);
                    calculatedStartTs = dayStartTs + (eta * 1000);
                    calculatedEndTs = dayStartTs + (etd * 1000);
                    hasRouteInfo = true;
                } else if (job.tsScheduled > 0) {
                    // Fall back to scheduled timestamp
                    calculatedStartTs = job.tsScheduled;
                    calculatedEndTs = job.tsScheduled + (job.onSiteSeconds * 1000 || 30 * 60 * 1000);
                } else {
                    // Default to 9 AM on the job's day
                    const dayStartTs = dayToMillis(day);
                    calculatedStartTs = dayStartTs + (9 * 60 * 60 * 1000); // 9 AM
                    calculatedEndTs = calculatedStartTs + 30 * 60 * 1000; // 30 min default
                }
                
                results.push({
                    ...job,
                    calculatedStartTs,
                    calculatedEndTs,
                    hasRouteInfo
                });
            });
        }
        
        return results;
    }
});

// Helper function to calculate ETA/ETD for a job from route info
function calcEtaEtdForJob(routeInfo: RouteInfo, jobNumber: number): { eta: number; etd: number } {
    const startTime = routeInfo.metaStartTime;
    let driveTime = 0;
    let placeTime = 0;
    let waitTime = 0;
    
    for (let i = 0; i < jobNumber && i < routeInfo.legDistancesInKM.length && i < routeInfo.legTimesInSec.length; i++) {
        driveTime += routeInfo.legTimesInSec[i];
        waitTime += routeInfo.legWaitInSec?.[i] ?? 0;
        waitTime += routeInfo.legBreakInSec?.[i] ?? 0;
        if (i - 1 >= 0 && i - 1 < routeInfo.metaOnSiteTimesInSec.length) {
            placeTime += routeInfo.metaOnSiteTimesInSec[i - 1];
        }
    }
    
    const eta = startTime + driveTime + placeTime + waitTime;
    let etd = eta;
    if (routeInfo.metaOnSiteTimesInSec.length > jobNumber - 1) {
        etd += routeInfo.metaOnSiteTimesInSec[jobNumber - 1];
    }
    
    return { eta, etd };
}

// Helper to convert Day to millisecond timestamp at midnight
function dayToMillis(day: Day): number {
    const dayString = `${day}`;
    const year = Number(dayString.substring(0, 4));
    const month = Number(dayString.substring(4, 6)) - 1;
    const dayNum = Number(dayString.substring(6, 8));
    return new Date(year, month, dayNum, 0, 0, 0, 0).getTime();
}
